/**
 * Life4Earth V2 — Login Page
 *
 * Displays Google and Apple sign-in buttons.
 * Handles OAuth flow and redirects to the app on success.
 *
 * Google Sign-In uses the @react-oauth/google library.
 * Apple Sign-In uses Apple's JS SDK loaded via script tag.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { useAuth } from './AuthContext';
import TotpVerifyScreen from '../Components/TotpSetup/TotpVerifyScreen';

// ── Styles ──────────────────────────────────────────────────

const containerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  background: 'linear-gradient(135deg, var(--bg-primary) 0%, var(--bg-secondary) 50%, var(--bg-primary) 100%)',
  fontFamily: 'system-ui, -apple-system, sans-serif',
};

const cardStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 400,
  padding: '48px 36px',
  background: 'var(--bg-elevated)',
  backdropFilter: 'blur(20px)',
  borderRadius: 16,
  border: '1px solid var(--bg-active)',
  boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
};

const logoStyle: React.CSSProperties = {
  textAlign: 'center',
  marginBottom: 32,
};

const titleStyle: React.CSSProperties = {
  fontSize: 28,
  fontWeight: 700,
  color: 'var(--text-primary)',
  margin: '0 0 8px 0',
};

const subtitleStyle: React.CSSProperties = {
  fontSize: 14,
  color: 'var(--text-tertiary)',
  margin: 0,
};

const dividerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  margin: '24px 0',
  color: 'var(--text-muted)',
  fontSize: 12,
};

const lineStyle: React.CSSProperties = {
  flex: 1,
  height: 1,
  background: 'var(--bg-active)',
};

const errorStyle: React.CSSProperties = {
  padding: '12px 16px',
  background: 'var(--error-bg)',
  border: '1px solid rgba(239, 68, 68, 0.2)',
  borderRadius: 8,
  color: '#fca5a5',
  fontSize: 13,
  marginBottom: 16,
};

const btnBase: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 12,
  width: '100%',
  padding: '14px 20px',
  border: 'none',
  borderRadius: 10,
  fontSize: 15,
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'all 0.15s ease',
};

const googleBtnStyle: React.CSSProperties = {
  ...btnBase,
  background: '#ffffff',
  color: '#1f2937',
  marginBottom: 12,
};

const appleBtnStyle: React.CSSProperties = {
  ...btnBase,
  background: '#000000',
  color: '#ffffff',
};

// ── Google SVG Icon ─────────────────────────────────────────

function GoogleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 48 48">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}

// ── Apple SVG Icon ──────────────────────────────────────────

function AppleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
      <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.32 2.32-2.11 4.45-3.74 4.25z"/>
    </svg>
  );
}

// ── Email form styles ────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 14px',
  border: '1px solid var(--bg-active)',
  borderRadius: 8,
  background: 'var(--bg-primary)',
  color: 'var(--text-primary)',
  fontSize: 14,
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color 0.15s ease',
};

const emailBtnStyle: React.CSSProperties = {
  ...btnBase,
  background: 'var(--accent-primary)',
  color: '#ffffff',
};

const linkBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'var(--accent-primary)',
  fontSize: 13,
  cursor: 'pointer',
  padding: 0,
  textDecoration: 'underline',
};

const successStyle: React.CSSProperties = {
  padding: '12px 16px',
  background: 'rgba(34, 197, 94, 0.1)',
  border: '1px solid rgba(34, 197, 94, 0.2)',
  borderRadius: 8,
  color: '#86efac',
  fontSize: 13,
  marginBottom: 16,
};

// ── Component ───────────────────────────────────────────────

type AuthView = 'login' | 'forgot-password';

export default function LoginPage() {
  const { t } = useAppTranslation('pages');
  const { login, emailLogin, forgotPassword, isAuthenticated, requires2FA } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [loading, setLoading] = useState<'google' | 'apple' | 'email' | null>(null);
  const [view, setView] = useState<AuthView>('login');

  // Form fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Redirect if already authenticated (but not if we're in the middle of a 2FA challenge)
  const from = (location.state as any)?.from?.pathname || '/';
  useEffect(() => {
    if (isAuthenticated && !requires2FA) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, requires2FA, navigate, from]);

  // Google Sign-In callback
  const handleGoogleLogin = useCallback(async () => {
    setError(null);
    setLoading('google');

    try {
      // Use Google's One Tap / button flow via google.accounts.id
      const google = (window as any).google;
      if (!google?.accounts?.id) {
        throw new Error('Google Sign-In SDK not loaded. Check your internet connection.');
      }

      // Trigger the Google Sign-In popup
      google.accounts.id.initialize({
        client_id: (window as any).__L4E_GOOGLE_CLIENT_ID || '',
        callback: async (response: any) => {
          try {
            const result = await login('google', response.credential);
            if (!result.requires2FA) {
              navigate(from, { replace: true });
            }
            // If 2FA required, LoginPage re-renders and shows TotpVerifyScreen
          } catch (err: any) {
            setError(err.message || 'Login failed');
            setLoading(null);
          }
        },
      });

      google.accounts.id.prompt((notification: any) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          // One Tap not available — fall back to popup
          google.accounts.id.renderButton(
            document.getElementById('google-signin-btn'),
            { type: 'standard', size: 'large', width: '100%' }
          );
          // Click the rendered button
          const btn = document.querySelector('#google-signin-btn div[role="button"]') as HTMLElement;
          if (btn) btn.click();
          else setLoading(null);
        }
      });
    } catch (err: any) {
      setError(err.message || 'Google login failed');
      setLoading(null);
    }
  }, [login, navigate, from]);

  // Apple Sign-In callback
  const handleAppleLogin = useCallback(async () => {
    setError(null);
    setLoading('apple');

    try {
      const AppleID = (window as any).AppleID;
      if (!AppleID?.auth) {
        throw new Error('Apple Sign-In SDK not loaded. Check your internet connection.');
      }

      const response = await AppleID.auth.signIn();
      const result = await login('apple', response.authorization.id_token);
      if (!result.requires2FA) {
        navigate(from, { replace: true });
      }
    } catch (err: any) {
      if (err.error === 'popup_closed_by_user') {
        setLoading(null);
        return;
      }
      setError(err.message || 'Apple login failed');
      setLoading(null);
    }
  }, [login, navigate, from]);

  // Email/password login
  const handleEmailLogin = useCallback(async () => {
    setError(null);
    setSuccessMsg(null);
    setLoading('email');
    try {
      const result = await emailLogin(email, password);
      if (!result.requires2FA) {
        navigate(from, { replace: true });
      }
    } catch (err: any) {
      setError(err.message || 'Login failed');
      setLoading(null);
    }
  }, [emailLogin, email, password, navigate, from]);

  // Forgot password
  const handleForgotPassword = useCallback(async () => {
    setError(null);
    setSuccessMsg(null);
    setLoading('email');
    try {
      const msg = await forgotPassword(email);
      setSuccessMsg(msg);
      setLoading(null);
    } catch (err: any) {
      setError(err.message || 'Request failed');
      setLoading(null);
    }
  }, [forgotPassword, email]);

  const switchView = (v: AuthView) => {
    setView(v);
    setError(null);
    setSuccessMsg(null);
  };

  // Show TOTP verification screen when 2FA is required
  if (requires2FA) {
    return <TotpVerifyScreen />;
  }

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={logoStyle}>
          {/* L4E logo placeholder — replace with actual SVG */}
          <div style={{
            width: 64,
            height: 64,
            background: 'linear-gradient(135deg, var(--accent-primary) 0%, var(--color-success) 100%)',
            borderRadius: 16,
            margin: '0 auto 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 28,
            fontWeight: 800,
            color: 'white',
          }}>
            L4E
          </div>
          <h1 style={titleStyle}>Life4Earth</h1>
          <p style={subtitleStyle}>{t('login.subtitle')}</p>
        </div>

        {error && <div style={errorStyle}>{error}</div>}
        {successMsg && <div style={successStyle}>{successMsg}</div>}

        {/* ── OAuth buttons (always visible on login view) ───── */}
        {view === 'login' && (
          <>
            {/* Hidden container for Google's rendered button (fallback) */}
            <div id="google-signin-btn" style={{ display: 'none' }} />

            <button
              style={{ ...googleBtnStyle, opacity: loading === 'google' ? 0.7 : 1 }}
              onClick={handleGoogleLogin}
              disabled={loading !== null}
            >
              <GoogleIcon />
              {loading === 'google' ? 'Signing in...' : 'Sign in with Google'}
            </button>

            <button
              style={{ ...appleBtnStyle, opacity: loading === 'apple' ? 0.7 : 1 }}
              onClick={handleAppleLogin}
              disabled={loading !== null}
            >
              <AppleIcon />
              {loading === 'apple' ? 'Signing in...' : 'Sign in with Apple'}
            </button>

            <div style={dividerStyle}>
              <span style={lineStyle} />
              <span>or sign in with email</span>
              <span style={lineStyle} />
            </div>
          </>
        )}

        {/* ── Email/Password Form ────────────────────────────── */}
        {view === 'login' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={e => setEmail(e.target.value)}
              style={inputStyle}
              autoComplete="email"
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              style={inputStyle}
              autoComplete="current-password"
              onKeyDown={e => e.key === 'Enter' && handleEmailLogin()}
            />
            <button
              style={{ ...emailBtnStyle, opacity: loading === 'email' ? 0.7 : 1 }}
              onClick={handleEmailLogin}
              disabled={loading !== null || !email || !password}
            >
              {loading === 'email' ? 'Signing in...' : 'Sign in with Email'}
            </button>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
              <button style={linkBtnStyle} onClick={() => switchView('forgot-password')}>
                Forgot password?
              </button>
              <Link to="/register" style={{ ...linkBtnStyle, textDecoration: 'underline' }}>
                Create account
              </Link>
            </div>
          </div>
        )}

        {/* ── Forgot Password Form ───────────────────────────── */}
        {view === 'forgot-password' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <h2 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 4px 0' }}>
              Reset Password
            </h2>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>
              Enter your email address and we'll send you a link to reset your password.
            </p>
            <input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={e => setEmail(e.target.value)}
              style={inputStyle}
              autoComplete="email"
              onKeyDown={e => e.key === 'Enter' && handleForgotPassword()}
            />
            <button
              style={{ ...emailBtnStyle, opacity: loading === 'email' ? 0.7 : 1 }}
              onClick={handleForgotPassword}
              disabled={loading !== null || !email}
            >
              {loading === 'email' ? 'Sending...' : 'Send Reset Link'}
            </button>
            <button style={{ ...linkBtnStyle, textAlign: 'center', marginTop: 4 }} onClick={() => switchView('login')}>
              Back to sign in
            </button>
          </div>
        )}

        <div style={dividerStyle}>
          <span style={lineStyle} />
          <span>{t('login.invitationOnly')}</span>
          <span style={lineStyle} />
        </div>

        <p style={{
          textAlign: 'center',
          fontSize: 12,
          color: 'var(--text-muted)',
          margin: 0,
          lineHeight: 1.5,
        }}>
          Life4Earth is currently in private beta.
          <br />
          Contact your administrator for access.
        </p>
      </div>
    </div>
  );
}
